/**
 * Checklist Service (Field-side)
 *
 * Provides the field-facing flows for working a checklist on a Work
 * Order in progress: load the snapshotted instance + items, toggle
 * completion, capture verification photos at sign-off, and submit
 * the final sign-off with a signature.
 *
 * As of the CMS rollout the underlying schema is the polymorphic
 * ChecklistInstance + ChecklistItem (snapshotted from a Checklist
 * template at assignment time). This service composes data from two
 * server endpoints into the WorkOrderChecklist shape that the
 * existing useWorkOrderWorkflow hook + WorkflowChecklist UI consume:
 *
 *   - GET /work-orders/{id}                  — verify/submit fields,
 *                                              assignees, signature URL
 *   - GET /work-orders/{id}/checklist        — instance + items
 *   - POST /checklist-items/{id}/toggle      — flip completion
 *
 * The verify and submit endpoints (verify, submit) reference fields
 * on the work_orders table (checklist_verified, checklist_submitted,
 * etc.) that are populated by a server-side flow not yet implemented.
 * Those service methods remain in place for future wire-up.
 */

import { api, camelizeKeys } from './api';
import { ratingMeta } from '../utils/inspectionRating';
import type { InspectionRating } from '../utils/inspectionRating';
import type { ApiResponse } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * A single checklist item rendered in the field workflow.
 *
 * The instance schema replaces the legacy `label` + `category` fields
 * with `title`, `details`, `additionalDetails`, plus behavior flags
 * (`photoMode` + `photoRequired`, `isRequired`). For backward
 * compatibility we keep `label` populated (mirrors `title`) so any
 * UI not yet migrated still renders something sensible.
 */
/**
 * Inspection rating, set per-item on inspection-typed instances. Stays
 * NULL on work-order-typed instances (which use isCompleted instead).
 */
export type ChecklistItemRating = InspectionRating;

/**
 * Photo capture mode for a single checklist item. Drives which camera
 * affordances the field UI surfaces:
 *   - 'none'         no camera UI at all
 *   - 'photo'        a single camera button (capture one photo)
 *   - 'before_after' two camera buttons (Before + After), one slot each
 */
export type ChecklistItemPhotoMode = 'none' | 'photo' | 'before_after';

/**
 * Per-item, per-phase set tracking which slots the *current session*
 * has captured a photo for. The local "Done" state in the workflow
 * UIs unions this with the server-persisted item.photos array so the
 * UI flips optimistically without waiting for the refetch.
 *
 * The 'single' key is used for items in photoMode='photo'; 'before'
 * and 'after' are used for photoMode='before_after'.
 */
export type CapturedPhotoPhases = { single?: boolean; before?: boolean; after?: boolean };

/**
 * Reports whether an item's photo requirement has been satisfied
 * (combining server-persisted photos with this-session captures).
 *
 *   - mode='none' OR photoRequired=false → always satisfied.
 *   - mode='photo'        → any attached photo OR a 'single' capture.
 *   - mode='before_after' → BOTH a before AND an after must exist
 *                           (either persisted or captured this session).
 *
 * Centralized here so the WO + Inspection workflow paths agree on
 * what "done" means without duplicating the rule in three places.
 */
export function isItemPhotoSatisfied(
    item: Pick<ChecklistItem, 'photoMode' | 'photoRequired' | 'photos'>,
    captured?: CapturedPhotoPhases,
): boolean {
    if (item.photoMode === 'none' || !item.photoRequired) return true;
    if (item.photoMode === 'photo') {
        return (item.photos?.length ?? 0) > 0 || !!captured?.single;
    }
    // before_after
    const hasBefore =
        (item.photos ?? []).some((p) => p.photoPhase === 'before') || !!captured?.before;
    const hasAfter =
        (item.photos ?? []).some((p) => p.photoPhase === 'after') || !!captured?.after;
    return hasBefore && hasAfter;
}

/**
 * For a required-photo item that isn't yet satisfied, reports which
 * capture slot the field UI should open next when it redirects a tech
 * to the camera:
 *   - 'photo' mode        → undefined (a single, un-phased shot)
 *   - 'before_after' mode → 'before' until the before slot is filled,
 *                           then 'after'
 *
 * Returns undefined for 'none'/single-photo items. Callers should gate
 * on isItemPhotoSatisfied first — this only picks the slot once we know
 * a required photo is still missing. Kept beside isItemPhotoSatisfied so
 * the WO + Inspection paths share one definition of "the next slot".
 */
export function nextUnfilledPhotoPhase(
    item: Pick<ChecklistItem, 'photoMode' | 'photos'>,
    captured?: CapturedPhotoPhases,
): 'before' | 'after' | undefined {
    if (item.photoMode !== 'before_after') return undefined;
    const hasBefore =
        (item.photos ?? []).some((p) => p.photoPhase === 'before') || !!captured?.before;
    return hasBefore ? 'after' : 'before';
}

export interface ChecklistItem {
    id: string;
    /** New display name — preferred for new UI */
    title: string;
    /** Legacy alias of title — kept for compatibility with older render code */
    label: string;
    /** Primary description text (auto-grow textarea on the CMS side) */
    details: string | null;
    /** Optional secondary callout shown beneath details */
    additionalDetails: string | null;
    /**
     * What kind of photo input the field UI should render for this
     * item. See ChecklistItemPhotoMode for the enum semantics.
     */
    photoMode: ChecklistItemPhotoMode;
    /**
     * When true, completion is blocked until the photo(s) implied by
     * photoMode are attached. For 'before_after' that means BOTH slots
     * are filled; for 'photo' that means at least one photo is
     * attached; ignored when mode='none'.
     */
    photoRequired: boolean;
    /** When true, sign-off is blocked until this item is completed */
    isRequired: boolean;
    isCompleted: boolean;
    /**
     * Inspection rating — only set on inspection-typed instances.
     * Null on work-order-typed instances and on un-rated rows.
     */
    rating: ChecklistItemRating | null;
    /**
     * Why this tier was chosen — optional for Good / Needs Attention,
     * required for Critical. This is the column the server writes today
     * (rating_note); every surface that shows the inspector's comment
     * reads it from here.
     */
    ratingNote: string | null;
    /**
     * Retired four-state note column. Only historical rows carry it; the
     * adapter folds it into ratingNote so readers never need a fallback.
     */
    dissatisfiedNote: string | null;
    completedById: string | null;
    completedByName: string | null;
    completedAt: string | null;
    sortOrder: number;
    /** Legacy grouping label — always null on instance items, kept for type compat */
    category: string | null;
    /**
     * Photos attached to this specific checklist item.
     *
     * Populated server-side from `work_orders.photos` filtered by
     * `checklist_item_id`. Empty array when nothing has been captured
     * yet for this item. Drives the inline-thumbnail + fullscreen
     * viewer in the field workflow so techs can confirm what they
     * just photographed without leaving the checklist.
     */
    photos: ChecklistItemPhoto[];
}

/**
 * Photo attached to a single checklist item via the `checklist_item_id`
 * FK on the work_order_photos / inspection_photos tables.
 */
export interface ChecklistItemPhoto {
    id: string;
    /** Absolute URL — uploadService composes this on store. */
    url: string;
    /**
     * Phase tag for items in photoMode='before_after'. Null on any
     * photo captured against a single-photo item (mode='photo') or
     * uploaded before this feature shipped.
     */
    photoPhase: 'before' | 'after' | null;
    /** Free-form caption from the capture modal "Details" input. */
    caption: string | null;
    /** Free-form area from the capture modal — e.g. "Entry", "Floor 2". */
    area: string | null;
    createdAt: string | null;
}

/**
 * Full checklist state for a work order, including verification and sign-off status.
 */
export interface WorkOrderChecklist {
    workOrderId: string;
    items: ChecklistItem[];
    isVerified: boolean;
    verifiedById: string | null;
    verifiedByName: string | null;
    verifiedAt: string | null;
    isSubmitted: boolean;
    submittedById: string | null;
    submittedByName: string | null;
    submittedAt: string | null;
    signatureDataUrl: string | null;
    verificationPhotos: VerificationPhoto[];
    assignees: ChecklistAssignee[];
    /** Source template metadata when the instance was created from a CMS template */
    sourceChecklist?: { id: string; title: string; version: number; isActive: boolean } | null;
}

export interface VerificationPhoto {
    id: string;
    url: string;
    caption: string;
    createdAt: string;
}

export interface ChecklistAssignee {
    userId: string;
    name: string;
    isPrimary: boolean;
}

export interface ToggleChecklistItemData {
    isCompleted: boolean;
}

export interface VerifyChecklistData {
    photos: { base64: string; caption?: string }[];
}

export interface SubmitChecklistData {
    signatureDataUrl: string;
    notes?: string;
}

/** What the server reports back after emailing the on-site contact. */
export interface ContactVerificationResult {
    contactNotified: boolean;
    contactEmail: string | null;
    contactNotifiedAt: string | null;
    /** True when the contact had already been asked before this send. */
    resend: boolean;
}

/* ============================================
 * INTERNAL HELPERS
 * ============================================ */

/**
 * Coerce a server-side instance item row (post-camelize) into the
 * ChecklistItem shape consumed by the field UI. Mirrors `title` to
 * `label` for backward compatibility with older render code.
 *
 * `photos` is filled in later by the getChecklist composer once it
 * has the parent record's photo collection — the instance endpoint
 * doesn't carry photos, only items.
 */
function adaptInstanceItem(it: any): ChecklistItem {
    const completedBy = it.completedBy ?? null;
    const completedByName = completedBy
        ? `${completedBy.firstName ?? ''} ${completedBy.lastName ?? ''}`.trim() || null
        : null;
    /* Coerce rating to one of the three tiers, or null. Defensive because the
     * server may not send the column on older WO-typed payloads, and because
     * a historical row could still carry a retired label. */
    const rawRating = it.rating;
    const rating: ChecklistItemRating | null = ratingMeta(rawRating)?.value ?? null;
    /* Defend against an unknown / missing mode value by collapsing
     * anything outside the three enums to 'none'. The server validates
     * the enum at the API edge, but a snapshot from a pre-feature
     * instance may not carry the column at all. */
    const rawMode = it.photoMode;
    const photoMode: ChecklistItemPhotoMode =
        rawMode === 'photo' || rawMode === 'before_after' ? rawMode : 'none';
    return {
        id: String(it.id),
        title: String(it.title ?? it.label ?? ''),
        label: String(it.title ?? it.label ?? ''),
        details: it.details ?? null,
        additionalDetails: it.additionalDetails ?? null,
        photoMode,
        photoRequired: !!it.photoRequired,
        isRequired: !!it.isRequired,
        isCompleted: !!it.isCompleted,
        rating,
        /* The server stores the comment in rating_note. This adapter used to
         * copy only the retired dissatisfied_note column, so every refetch
         * after a save handed the picker an empty note and wiped what the
         * inspector had just typed (beta feedback: "the comment disappears
         * when you move to the next item"). Historical rows that only have
         * the old column still show their text through the fallback. */
        ratingNote: it.ratingNote ?? it.dissatisfiedNote ?? null,
        dissatisfiedNote: it.dissatisfiedNote ?? null,
        completedById: it.completedById ? String(it.completedById) : null,
        completedByName,
        completedAt: it.completedAt ?? null,
        sortOrder: Number(it.sortOrder ?? 0),
        category: null,
        photos: [],
    };
}

/**
 * Group a flat list of work_order_photos / inspection_photos rows
 * (post-camelize) by their `checklistItemId` so we can attach each
 * photo array onto its matching ChecklistItem.
 *
 * Photos with no checklist_item_id (verification photos, ad-hoc
 * captures from older flows) are ignored here — they belong to the
 * verificationPhotos collection on the parent payload, not to a
 * specific item.
 */
function groupPhotosByItem(rawPhotos: any[]): Map<string, ChecklistItemPhoto[]> {
    const grouped = new Map<string, ChecklistItemPhoto[]>();
    if (!Array.isArray(rawPhotos)) return grouped;
    for (const p of rawPhotos) {
        const itemId = p.checklistItemId ?? p.checklist_item_id;
        if (itemId === null || itemId === undefined) continue;
        const key = String(itemId);
        // Normalize photoPhase to the known enum or null. Anything
        // unexpected (e.g. an old snapshot or a bug elsewhere) falls
        // through as null so the UI treats it as a generic attachment.
        const rawPhase = p.photoPhase ?? p.photo_phase ?? null;
        const photoPhase: 'before' | 'after' | null =
            rawPhase === 'before' || rawPhase === 'after' ? rawPhase : null;

        const photo: ChecklistItemPhoto = {
            id: String(p.id),
            url: String(p.photoUrl ?? p.photo_url ?? p.url ?? ''),
            photoPhase,
            caption: p.caption ?? null,
            area: p.area ?? null,
            createdAt: p.createdAt ?? p.created_at ?? null,
        };
        const arr = grouped.get(key) ?? [];
        arr.push(photo);
        grouped.set(key, arr);
    }
    return grouped;
}

/**
 * Map a Work Order's `assignees` relation (post-camelize) into the
 * ChecklistAssignee[] shape needed by the workflow hook for
 * canActAsPrimary detection.
 */
function adaptAssignees(rawAssignees: any[]): ChecklistAssignee[] {
    if (!Array.isArray(rawAssignees)) return [];
    return rawAssignees.map((a: any) => {
        const first = a.firstName ?? a.first_name ?? '';
        const last = a.lastName ?? a.last_name ?? '';
        const name = `${first} ${last}`.trim() || a.email || `User #${a.id}`;
        // The pivot may be camelized (isPrimary) or snake (is_primary)
        const pivot = a.pivot ?? {};
        return {
            userId: String(a.id),
            name,
            isPrimary: !!(pivot.isPrimary ?? pivot.is_primary),
        };
    });
}

/* ============================================
 * SERVICE METHODS
 * ============================================ */

export const checklistService = {
    /**
     * Compose the WorkOrderChecklist shape from two endpoints.
     *
     * Even though the hook expects a single fetch, two server calls
     * is acceptable here because:
     *   - The endpoints are cheap (both read a single record + relations).
     *   - Composing on the client keeps the backend surface narrow.
     *   - The polling interval is 10s, which is plenty of headroom.
     */
    getChecklist: async (workOrderId: string): Promise<ApiResponse<WorkOrderChecklist>> => {
        const [woRes, instRes] = await Promise.all([
            api.get<any>(`/work-orders/${workOrderId}`),
            api.get<any>(`/work-orders/${workOrderId}/checklist`),
        ]);

        /* The api client returns raw snake_case JSON; camelize each
         * payload here so adaptInstanceItem + the WO field reads
         * resolve under their expected camelCase names. Without
         * this, isRequired / photoMode / sortOrder all come back
         * undefined and the field UX renders without the Required
         * + camera-required badges. */
        const woWrapper = camelizeKeys(woRes.data) as any;
        const instWrapper = camelizeKeys(instRes.data) as any;
        const wo = woWrapper?.data ?? woWrapper;
        const instance = instWrapper?.data ?? null;

        const items: ChecklistItem[] = ((instance?.items ?? []) as any[]).map(adaptInstanceItem);

        /* Attach per-item photos. The WO show endpoint eager-loads
         * `photos` on the work order, each carrying an optional
         * `checklist_item_id` FK pointing back at the items above.
         * Grouping client-side keeps the backend response shape
         * unchanged while letting the field UI render thumbnails
         * inline next to the item that owns them. */
        const photosByItem = groupPhotosByItem(wo?.photos ?? []);
        for (const item of items) {
            item.photos = photosByItem.get(item.id) ?? [];
        }

        const checklist: WorkOrderChecklist = {
            workOrderId: String(wo?.id ?? workOrderId),
            items,
            isVerified: !!(wo?.checklistVerified ?? wo?.checklist_verified),
            verifiedById: wo?.checklistVerifiedById
                ? String(wo.checklistVerifiedById)
                : (wo?.checklist_verified_by_id ? String(wo.checklist_verified_by_id) : null),
            verifiedByName: null,
            verifiedAt: wo?.checklistVerifiedAt ?? wo?.checklist_verified_at ?? null,
            isSubmitted: !!(wo?.checklistSubmitted ?? wo?.checklist_submitted),
            submittedById: wo?.checklistSubmittedById
                ? String(wo.checklistSubmittedById)
                : (wo?.checklist_submitted_by_id ? String(wo.checklist_submitted_by_id) : null),
            submittedByName: null,
            submittedAt: wo?.checklistSubmittedAt ?? wo?.checklist_submitted_at ?? null,
            signatureDataUrl: wo?.signatureUrl ?? wo?.signatureData ?? wo?.signature_url ?? wo?.signature_data ?? null,
            verificationPhotos: [],
            assignees: adaptAssignees(wo?.assignees ?? []),
            sourceChecklist: instance?.sourceChecklist
                ? {
                    id: String(instance.sourceChecklist.id),
                    title: instance.sourceChecklist.title,
                    version: instance.sourceChecklist.version,
                    isActive: !!instance.sourceChecklist.isActive,
                }
                : null,
        };

        return {
            data: checklist,
            status: instRes.status,
            ok: instRes.ok,
        };
    },

    /**
     * Inspection-side composer. Mirrors getChecklist() but reads
     * the inspection record + its checklist instance, attaching
     * each photo with a checklist_item_id back onto its parent
     * item. Returns just the items array — inspection flows don't
     * need the verify/submit fields, so we keep the response shape
     * minimal here.
     *
     * Photos on the inspection record carry a checklist_item_id
     * FK populated when uploadService.uploadInspectionPhoto is
     * called with `{ checklistItemId }` in the metadata. Photos
     * uploaded for the legacy observation/correction flows leave
     * checklist_item_id NULL and are skipped here (they show in
     * the Observations / Completed QC tabs instead).
     */
    getInspectionChecklist: async (inspectionId: string): Promise<ApiResponse<{ items: ChecklistItem[] }>> => {
        const [inspRes, instRes] = await Promise.all([
            api.get<any>(`/inspections/${inspectionId}`),
            api.get<any>(`/inspections/${inspectionId}/checklist`),
        ]);

        const inspWrapper = camelizeKeys(inspRes.data) as any;
        const instWrapper = camelizeKeys(instRes.data) as any;
        const inspection = inspWrapper?.data ?? inspWrapper;
        const instance = instWrapper?.data ?? null;

        const items: ChecklistItem[] = ((instance?.items ?? []) as any[]).map(adaptInstanceItem);

        const photosByItem = groupPhotosByItem(inspection?.photos ?? []);
        for (const item of items) {
            item.photos = photosByItem.get(item.id) ?? [];
        }

        return {
            data: { items },
            status: instRes.status,
            ok: instRes.ok,
        };
    },

    /**
     * Toggle a single checklist item's completion via the new
     * polymorphic endpoint. Returns the adapted item shape.
     */
    toggleItem: async (
        _workOrderId: string,
        itemId: string,
        data: ToggleChecklistItemData,
    ): Promise<ApiResponse<ChecklistItem>> => {
        const res = await api.post<any>(`/checklist-items/${itemId}/toggle`, {
            isCompleted: data.isCompleted,
        });
        /* Camelize the toggle response too so optimistic-state replacement
         * sees the same field names as the initial fetch. */
        const camelized = camelizeKeys(res.data) as any;
        const it = camelized?.data ?? camelized;
        return {
            data: adaptInstanceItem(it),
            status: res.status,
            ok: res.ok,
        };
    },

    /**
     * Verify endpoint — POSTs the (optional) verification photos and
     * marks the WO's checklist as verified server-side. The response
     * is a thin { verified, verified_at } payload, so we then refetch
     * the full WorkOrderChecklist via getChecklist() and hand THAT
     * back to the caller. This way the hook's setChecklist(response.data)
     * receives the freshly-derived shape (including the updated
     * isVerified flag and any newly-saved verificationPhotos) without
     * the controller having to redundantly compose the full payload.
     */
    verify: async (
        workOrderId: string,
        data: VerifyChecklistData,
    ): Promise<ApiResponse<WorkOrderChecklist>> => {
        await api.post(`/work-orders/${workOrderId}/checklist/verify`, data);
        return checklistService.getChecklist(workOrderId);
    },

    /**
     * Sign-off submission — same pattern as verify(). Server applies
     * the signature + notes + status transition to 'completed';
     * client refetches the composed checklist payload.
     */
    submit: async (
        workOrderId: string,
        data: SubmitChecklistData,
    ): Promise<ApiResponse<WorkOrderChecklist>> => {
        await api.post(`/work-orders/${workOrderId}/checklist/submit`, data);
        return checklistService.getChecklist(workOrderId);
    },

    /**
     * Submit-for-verification — used when the on-site contact is NOT the
     * assignee. The technician finishes the checklist and hands off: the
     * server moves the WO into the in_review state and, when asked to, emails
     * the on-site contact to verify + sign off. No signature is captured here
     * (the contact provides it later, out of band). Returns the refreshed
     * checklist payload like verify()/submit().
     *
     * `notifyContact` is the answer the tech gave at the pre-submit
     * confirmation prompt. Pass false for "Don't email" — the work order still
     * goes to review, the customer just isn't contacted. Always sent
     * explicitly, so the server never has to fall back to its legacy default.
     * (The api client snakifies outgoing keys, so notifyContact arrives as
     * notify_contact.)
     */
    submitForVerification: async (
        workOrderId: string,
        options?: { notifyContact?: boolean },
    ): Promise<ApiResponse<WorkOrderChecklist>> => {
        await api.post(`/work-orders/${workOrderId}/checklist/submit-for-verification`, {
            notifyContact: options?.notifyContact !== false,
        });
        return checklistService.getChecklist(workOrderId);
    },

    /**
     * Ask the on-site contact to verify + sign, after the fact.
     *
     * The reviewer's half of the pre-submit prompt: when the tech chose "Don't
     * Email", this is how the customer gets asked once someone has actually
     * looked at the work. Also the resend path when the first request went
     * astray. Manager / admin / super_admin only — the server enforces it.
     *
     * Returns the send result (address used, the new timestamp, and whether
     * this was a resend) rather than a checklist payload — the caller is the
     * detail page, which refetches the work order itself.
     */
    requestContactVerification: async (
        workOrderId: string,
    ): Promise<ContactVerificationResult> => {
        const response = await api.post(`/work-orders/${workOrderId}/request-verification`, {});
        /* Responses arrive raw — the client snakifies outgoing bodies but
           leaves incoming keys alone — so unwrap the { data: … } envelope and
           camelize it here, the same way getChecklist() does. */
        const payload = (response.data as any)?.data ?? response.data;
        return camelizeKeys(payload) as ContactVerificationResult;
    },
};
